import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { useLocation, useParams } from 'react-router';
import Lottie from 'lottie-react';

import * as playlistActions from '@/playlist/Actions';
import { FRONTEND_ROUTES } from '@/services/Constants';
import { getFreePlayCategoryPath, getFreePlayExercisePath } from '@/pages/freePlay/Helpers';
import { showFireworksConfetti } from '@/pages/learningPath/Helper';
import { getInitialSnakePositions } from '@/playlist/games/letterParty/Helpers';
import useGameLayout from '@/playlist/games/common/hooks/useGameLayout';
import { useGameControls } from '@/playlist/games/letterParty/hooks/useGameControls';
import { useQuestionSetup } from '@/playlist/games/letterParty/hooks/useQuestionSetup';
import { useSnakeMovement } from '@/playlist/games/letterParty/hooks/useSnakeMovement';
import { useWordAudio } from '@/playlist/games/letterParty/hooks/useWordAudio';
import useAudioPlayer from '@/services/hocs/useAudioPlayer';
import useResponsive from '@/services/hocs/useResponsive';
import { GameStatus } from '@/playlist/games/letterParty/types';
import PlaylistExitContext from '@/playlist/PlaylistExitContext';
import { EndOutcome, SendAnswerRequest } from '@/playlist/types';
import { historyPush, historyReplace } from '@/services/History';

import ControlBar from '@/playlist/games/letterParty/components/controlBar/ControlBar';
import DjLevel from '@/playlist/games/letterParty/components/DjLevel';
import EndScreen from '@/playlist/games/letterParty/components/endScreen/EndScreen';
import FlyingLetter from '@/playlist/games/letterParty/components/FlyingLetter';
import GameBoard, {
	GameBoardHandle,
	GRID_HEIGHT_LANDSCAPE,
	GRID_HEIGHT_PORTRAIT,
	GRID_WIDTH_LANDSCAPE,
	GRID_WIDTH_PORTRAIT,
} from '@/playlist/games/letterParty/components/gameBoard/GameBoard';
import KeyInstructions from '@/playlist/games/letterParty/components/KeyInstructions';
import StartScreen from '@/playlist/games/letterParty/components/startScreen/StartScreen';

import SandBottom from '@/playlist/games/letterParty/assets/images/sand-bottom.svg?react';
import SandLeft from '@/playlist/games/letterParty/assets/images/sand-left.svg?react';
import SandRight from '@/playlist/games/letterParty/assets/images/sand-right.svg?react';
import Sandcastle from '@/playlist/games/letterParty/assets/images/sandcastle.svg?react';
import SwimmingFloat from '@/playlist/games/letterParty/assets/images/swimmingfloat.svg?react';
import palmTree from '@/playlist/games/letterParty/assets/lottie/palm-tree-level-1.json';
import speaker from '@/playlist/games/letterParty/assets/lottie/speaker.json';
import waves from '@/playlist/games/letterParty/assets/lottie/waves.json';
import confettiSound from '@/playlist/games/letterParty/assets/sounds/confetti.ogg';
import falseAnswerSound from '@/playlist/games/letterParty/assets/sounds/false-answer.ogg';
import goodAnswerSound from '@/playlist/games/letterParty/assets/sounds/good-answer.ogg';
import musicSound from '@/playlist/games/letterParty/assets/sounds/music.ogg';
import partyHatSound from '@/playlist/games/letterParty/assets/sounds/party-hat.ogg';
import wallHitSound from '@/playlist/games/letterParty/assets/sounds/wall-hit.ogg';

import '@/playlist/games/letterParty/styles/letter-party-game.scss';

import type { WordProgressHandle } from '@/playlist/games/letterParty/components/controlBar/WordProgress';
import type { Direction, Position } from '@/playlist/games/letterParty/types';
import type { PlayList, Question } from '@/playlist/types';

interface FlyingLetterAnimation {
	id: string;
	letter: string;
	letterIndex: number;
	from: { x: number; y: number };
	to: { x: number; y: number };
}

const DECAY_VIBE_INTERVAL_MS = 5000;
const GAME_LOOP_INTERVAL_MS = 545;

const SAFE_AREA_WIDTH = 5;
const SAFE_AREA_HEIGHT = 5;
const SEGMENTS_TO_REMOVE = 1;

type LetterPartyGameProps = {
	questions: Question[];
	playList: PlayList | null;
	levelProgress: any;
	isBridgePlaylist: boolean;
	sendAnswerRequest: SendAnswerRequest;
	finishPlaylistRequest: typeof playlistActions.finishPlaylistRequest;
	getPlaylistSuccess: typeof playlistActions.getPlaylistSuccess;
};

function LetterPartyGame({
	questions,
	playList,
	levelProgress,
	isBridgePlaylist,
	sendAnswerRequest,
	finishPlaylistRequest,
	getPlaylistSuccess,
}: LetterPartyGameProps) {
	useGameLayout();

	const { categoryId: categoryIdFromRoute, category: categorySlugFromRoute } = useParams<{
		categoryId?: string;
		category?: string;
	}>();
	const location = useLocation();
	const { isMobile } = useResponsive();

	const bestScore = playList?.game?.high_score || 3;

	// Detect orientation
	const [isPortrait, setIsPortrait] = useState(() => window.innerHeight > window.innerWidth);

	// Set grid dimensions based on orientation
	const GRID_WIDTH = isPortrait ? GRID_WIDTH_PORTRAIT : GRID_WIDTH_LANDSCAPE;
	const GRID_HEIGHT = isPortrait ? GRID_HEIGHT_PORTRAIT : GRID_HEIGHT_LANDSCAPE;

	const freePlayCategoryPath = useMemo(() => {
		if (categoryIdFromRoute && categorySlugFromRoute) {
			return getFreePlayCategoryPath(categoryIdFromRoute, categorySlugFromRoute);
		}
		return undefined;
	}, [categoryIdFromRoute, categorySlugFromRoute]);

	// Game state
	const [showStartScreen, setShowStartScreen] = useState(true);
	const [snake, setSnake] = useState<Position[]>(() => getInitialSnakePositions());
	const [collectedLetters, setCollectedLetters] = useState<string>('');
	const [collectedLetterSequence, setCollectedLetterSequence] = useState<string>('');
	const [revealedLetters, setRevealedLetters] = useState<string>('');
	const [gameStatus, setGameStatus] = useState<GameStatus>(GameStatus.Playing);
	const [isMusicMuted, setIsMusicMuted] = useState(false); // Music on by default
	const [vibeLevel, setVibeLevel] = useState(45); // Start at 45% (for music only)
	const [completedWord, setCompletedWord] = useState<string | null>(null); // For showing completed word
	const [hatCount, setHatCount] = useState(0);
	const [isRecovering, setIsRecovering] = useState(false); // Pause after taking damage
	const [isStopped, setIsStopped] = useState(false); // Snake stopped, waiting for new direction
	const [waitingForFirstDirection, setWaitingForFirstDirection] = useState(false); // Waiting for player to choose direction
	const [flyingLetters, setFlyingLetters] = useState<FlyingLetterAnimation[]>([]);
	const [showKeyInstructions, setShowKeyInstructions] = useState(false); // Show key instructions after inactivity
	const boardHandleRef = useRef<GameBoardHandle | null>(null);
	const wordProgressRef = useRef<WordProgressHandle | null>(null);
	const wordStartTimeRef = useRef<number | null>(null);
	const inactivityTimerRef = useRef<NodeJS.Timeout | null>(null);
	const hasAnsweredQuestionsRef = useRef(false);

	const directionRef = useRef<Direction>('RIGHT');
	const nextDirectionRef = useRef<Direction>('RIGHT');
	const hasInitializedRef = useRef(false);
	const snakeLength = snake.length;

	// Callbacks for board setup hook
	const handleResetSnake = useCallback(() => {
		setSnake(getInitialSnakePositions());
		setCollectedLetters('');
		setCollectedLetterSequence('');
		setRevealedLetters('');
		setFlyingLetters([]);
		setGameStatus(GameStatus.Playing);
		setWaitingForFirstDirection(true);
		wordStartTimeRef.current = Date.now();
	}, []);

	const handleResetGameState = useCallback(() => {
		setVibeLevel(45);
		setIsStopped(false);
		setHatCount(0);
	}, []);

	const handleResetWordProgress = useCallback(() => {
		setCollectedLetters('');
		setCollectedLetterSequence('');
		setRevealedLetters('');
		setFlyingLetters([]);
		wordStartTimeRef.current = Date.now();
	}, []);

	const handlePauseSnake = useCallback(() => {
		setWaitingForFirstDirection(true);
	}, []);

	// Question setup hook - handles word/letter/hat/confetti management
	const {
		currentWord,
		currentQuestionIndex,
		letterQs,
		hats,
		confettis,
		setLetterQs,
		setHats,
		setConfettis,
		startNewWord,
		initGame,
	} = useQuestionSetup({
		questions,
		snake,
		directionRef,
		nextDirectionRef,
		gridWidth: GRID_WIDTH,
		gridHeight: GRID_HEIGHT,
		safeAreaWidth: SAFE_AREA_WIDTH,
		safeAreaHeight: SAFE_AREA_HEIGHT,
		onResetSnake: handleResetSnake,
		onResetGameState: handleResetGameState,
		onResetWordProgress: handleResetWordProgress,
		onPauseSnake: handlePauseSnake,
	});

	// Detect orientation changes
	useEffect(() => {
		const handleOrientationChange = () => {
			setIsPortrait(window.innerHeight > window.innerWidth);
		};
		window.addEventListener('resize', handleOrientationChange);
		return () => window.removeEventListener('resize', handleOrientationChange);
	}, []);

	useEffect(() => {
		setHatCount((prev) => Math.min(prev, snakeLength));
	}, [snakeLength]);

	const { handlePlayWord, isWordAudioPlaying } = useWordAudio({
		currentWord,
		shouldAutoPlay: !showStartScreen,
	});

	const { play: playBackgroundMusic, pause: pauseBackgroundMusic } = useAudioPlayer({
		src: musicSound,
		loop: true,
		autoplay: true,
		volume: 0.3,
	});

	const goodAnswerAudio = useAudioPlayer({ src: goodAnswerSound, volume: 0.5 });
	const falseAnswerAudio = useAudioPlayer({ src: falseAnswerSound, volume: 0.5 });
	const partyHatAudio = useAudioPlayer({ src: partyHatSound, volume: 0.5 });
	const wallHitAudio = useAudioPlayer({ src: wallHitSound, volume: 1 });
	const confettiAudio = useAudioPlayer({ src: confettiSound, volume: 0.5 });

	useEffect(() => {
		if (isMusicMuted) {
			pauseBackgroundMusic();
		} else {
			playBackgroundMusic();
		}
	}, [isMusicMuted, playBackgroundMusic, pauseBackgroundMusic]);

	useEffect(() => {
		if (isWordAudioPlaying) {
			pauseBackgroundMusic();
		} else if (!isMusicMuted) {
			playBackgroundMusic();
		}
	}, [isWordAudioPlaying, isMusicMuted, playBackgroundMusic, pauseBackgroundMusic]);

	// Snake movement hook - handles direction and movement logic
	const { calculateNextHead } = useSnakeMovement({
		snake,
		directionRef,
		nextDirectionRef,
	});

	const toggleMute = useCallback((audioOn: boolean) => {
		setIsMusicMuted(!audioOn);
	}, []);

	const triggerLetterFlyAnimation = useCallback(
		(letter: string, letterIndex: number, gridPosition: Position) => {
			const targetRect = wordProgressRef.current?.getLetterBoxRect(letterIndex);
			const boardPosition = boardHandleRef.current?.getCellCenterPosition(gridPosition);

			if (!targetRect || !boardPosition) {
				return;
			}

			const { x: startX, y: startY } = boardPosition;
			const endX = targetRect.left + targetRect.width / 2;
			const endY = targetRect.top + targetRect.height / 2;
			const lowercaseLetter = letter.toLowerCase();

			setFlyingLetters((prev) => [
				...prev,
				{
					id: `${lowercaseLetter}-${letterIndex}-${Date.now()}`,
					letter: lowercaseLetter,
					letterIndex,
					from: { x: startX, y: startY },
					to: { x: endX, y: endY },
				},
			]);
		},
		[],
	);

	const handleRotateEntities = useCallback(
		(rotatePosition: (pos: Position) => Position) => {
			setSnake((prevSnake) => prevSnake.map(rotatePosition));

			setLetterQs((prevLetters) =>
				prevLetters.map((letter) => {
					const position = rotatePosition(letter.position);
					return {
						...letter,
						position,
						colorSeed: position.x * 100 + position.y,
					};
				}),
			);

			setHats((prevHats) =>
				prevHats.map((hat) => ({
					...hat,
					position: rotatePosition(hat.position),
				})),
			);

			setConfettis((prevConfettis) =>
				prevConfettis.map((confetti) => ({
					...confetti,
					position: rotatePosition(confetti.position),
				})),
			);
		},
		[setLetterQs, setHats, setConfettis],
	);

	const handleFlyingLetterComplete = useCallback(
		(id: string, letter: string, letterIndex: number) => {
			setFlyingLetters((prev) => prev.filter((animation) => animation.id !== id));
			setRevealedLetters((prev) => {
				if (prev.length > letterIndex) {
					return prev;
				}

				if (prev.length === letterIndex) {
					return `${prev}${letter.toLowerCase()}`;
				}

				return prev;
			});
		},
		[],
	);

	const handleFirstDirectionChosen = useCallback(() => {
		setWaitingForFirstDirection(false);
		setShowKeyInstructions(false);
	}, []);

	const handleResume = useCallback(() => {
		setIsStopped(false);
		setShowKeyInstructions(false);
	}, []);

	// Game controls hook - handles keyboard + touch/swipe input
	const swipeHandlers = useGameControls({
		gameStatus,
		waitingForFirstDirection,
		isRecovering,
		isStopped,
		directionRef,
		nextDirectionRef,
		onFirstDirectionChosen: handleFirstDirectionChosen,
		onResume: handleResume,
	});

	const startGame = useCallback(() => {
		initGame();
	}, [initGame]);

	const handleStartGame = useCallback(() => {
		setShowStartScreen(false);
	}, []);

	const onClose = useContext(PlaylistExitContext);
	const handleBack = useCallback(() => {
		if (onClose) {
			onClose();
			return;
		}
		if (freePlayCategoryPath) {
			historyPush(freePlayCategoryPath);
		} else {
			historyPush(FRONTEND_ROUTES.HOME);
		}
	}, [onClose, freePlayCategoryPath]);

	const handleReplay = useCallback(() => {
		const newUserLearningPathSection = levelProgress?.user_learning_path_section;
		if (newUserLearningPathSection && categoryIdFromRoute && categorySlugFromRoute) {
			setGameStatus(GameStatus.Playing);
			getPlaylistSuccess(newUserLearningPathSection);
			const newPath = getFreePlayExercisePath(
				categorySlugFromRoute,
				categoryIdFromRoute,
				'letterfeestje',
				newUserLearningPathSection.id,
			);
			historyReplace(newPath, { autostart: true });
		} else {
			handleBack();
		}
	}, [levelProgress, categoryIdFromRoute, categorySlugFromRoute, handleBack, getPlaylistSuccess]);

	useEffect(() => {
		const playlistId = playList?.id;
		// Only send finish request for GameOver (LOST)
		// Completed (SUCCEEDED) is handled by the callback after the last answer request
		// For bridge playlist, always send on GameOver (no answer requests are made mid-game)
		if (
			gameStatus === GameStatus.GameOver &&
			playlistId &&
			(isBridgePlaylist || hasAnsweredQuestionsRef.current)
		) {
			finishPlaylistRequest(playlistId, categoryIdFromRoute, EndOutcome.LOST);
		}
	}, [gameStatus, playList, categoryIdFromRoute, finishPlaylistRequest, isBridgePlaylist]);

	useEffect(() => {
		if (hasInitializedRef.current) return;
		if (!questions || questions.length === 0) return;

		hasInitializedRef.current = true;
		startGame();
	}, [startGame, questions]);

	// Auto-start game when replaying (skip start screen)
	useEffect(() => {
		const autostart = (location.state as any)?.autostart;
		if (autostart) {
			hasInitializedRef.current = false;
			hasAnsweredQuestionsRef.current = false;
			setShowStartScreen(false);
			setGameStatus(GameStatus.Playing);
			setSnake(getInitialSnakePositions());
			setCollectedLetters('');
			setCollectedLetterSequence('');
			setRevealedLetters('');
			setFlyingLetters([]);
			setCompletedWord(null);
			setHatCount(0);
			setIsRecovering(false);
			setIsStopped(false);
			setWaitingForFirstDirection(false);
			setVibeLevel(45);
			directionRef.current = 'RIGHT';
			nextDirectionRef.current = 'RIGHT';
		}
	}, [location.state]);

	// Game loop
	useEffect(() => {
		if (gameStatus !== GameStatus.Playing) return;
		if (waitingForFirstDirection) return; // Don't start until player chooses direction
		if (completedWord !== null) return; // Pause during word completion celebration
		if (isRecovering) return; // Pause during recovery after taking damage
		if (isStopped) return; // Pause when stopped, waiting for player input

		const gameLoop = setInterval(() => {
			// Update direction from ref
			const currentNextDirection = nextDirectionRef.current;
			directionRef.current = currentNextDirection;

			// Calculate new head position using hook
			setSnake((prevSnake) => {
				const newHead = calculateNextHead();

				// Check wall collision - remove Q's from polonaise based on length
				if (
					newHead.x < 0 ||
					newHead.x >= GRID_WIDTH ||
					newHead.y < 0 ||
					newHead.y >= GRID_HEIGHT
				) {
					const newSnakeAfterDamage = prevSnake.slice(
						0,
						prevSnake.length - SEGMENTS_TO_REMOVE,
					);

					// Check if game over (only leader left)
					if (newSnakeAfterDamage.length <= 1) {
						setGameStatus(GameStatus.GameOver);
						return prevSnake; // Keep current snake for display
					}

					// Play wall hit sound
					wallHitAudio.play();

					// Pause game briefly to show damage
					setIsRecovering(true);
					setTimeout(() => {
						setIsRecovering(false);
						setIsStopped(true); // Stop snake after recovery
					}, 1000);

					// Decrease vibe by 30%, but set to 5% if it goes to 0 or below
					setVibeLevel((prev) => {
						const newLevel = prev - 30;
						return newLevel <= 0 ? 5 : newLevel;
					});

					return newSnakeAfterDamage;
				}

				// Check self collision - remove all segments from hit point onwards
				const hitSegmentIndex = prevSnake.findIndex(
					(segment) => segment.x === newHead.x && segment.y === newHead.y,
				);

				if (hitSegmentIndex !== -1) {
					const newSnakeAfterDamage = prevSnake.slice(0, hitSegmentIndex);

					// Check if game over (only leader left or no segments)
					if (newSnakeAfterDamage.length <= 1) {
						setGameStatus(GameStatus.GameOver);
						return prevSnake; // Keep current snake for display
					}

					// Play wall hit sound
					wallHitAudio.play();

					// Pause game briefly to show damage
					setIsRecovering(true);
					setTimeout(() => {
						setIsRecovering(false);
						setIsStopped(true); // Stop snake after recovery
					}, 1000);

					// Decrease vibe by 30%, but set to 5% if it goes to 0 or below
					setVibeLevel((prev) => {
						const newLevel = prev - 30;
						return newLevel <= 0 ? 5 : newLevel;
					});

					return newSnakeAfterDamage;
				}

				// Check hat collection (leader pickup)
				const hatAtPosition = hats.find(
					(hat) =>
						!hat.collected &&
						hat.position.x === newHead.x &&
						hat.position.y === newHead.y,
				);

				if (hatAtPosition && prevSnake[0]) {
					setHatCount((prev) => Math.min(prevSnake.length, prev + 1));
					setHats((prevHats) =>
						prevHats.map((hat) =>
							hat.id === hatAtPosition.id ? { ...hat, collected: true } : hat,
						),
					);

					setVibeLevel((prev) => Math.min(100, prev + 5));
					partyHatAudio.play();
				}

				// Check confetti collection (leader pickup)
				const confettiAtPosition = confettis.find(
					(confetti) =>
						!confetti.collected &&
						confetti.position.x === newHead.x &&
						confetti.position.y === newHead.y,
				);

				if (confettiAtPosition && prevSnake[0]) {
					// Mark confetti as collected
					setConfettis((prevConfettis) =>
						prevConfettis.map((confetti) =>
							confetti.id === confettiAtPosition.id
								? { ...confetti, collected: true }
								: confetti,
						),
					);

					// Show confetti effect for 2 seconds
					showFireworksConfetti('game-confetti', 2000);
					confettiAudio.play();

					// Increase vibe level significantly
					setVibeLevel((prev) => Math.min(100, prev + 20));
				}

				// Check letter Q collection
				const nextLetterIndex = collectedLetters.length;
				const nextLetterNeeded = currentWord.toLowerCase()[nextLetterIndex];

				// Find if there's a letter at the new head position
				const letterAtPosition = letterQs.find(
					(letterQ) =>
						!letterQ.collected &&
						letterQ.position.x === newHead.x &&
						letterQ.position.y === newHead.y,
				);

				let shouldGrow = false;

				if (letterAtPosition) {
					const collectedLetterLower = letterAtPosition.letter.toLowerCase();
					setCollectedLetterSequence((prev) => `${prev}${collectedLetterLower}`);

					if (letterAtPosition.letter.toLowerCase() === nextLetterNeeded) {
						// Correct letter collected - add 1 Q to polonaise!
						triggerLetterFlyAnimation(
							letterAtPosition.letter,
							nextLetterIndex,
							letterAtPosition.position,
						);
						shouldGrow = true;
						setCollectedLetters((prev) => prev + letterAtPosition.letter);

						// Increase vibe level on correct letter
						setVibeLevel((prev) => Math.min(100, prev + 15));

						// Play party sound for correct letter!
						goodAnswerAudio.play();

						// Mark letter as collected
						setLetterQs((prevLetterQs) =>
							prevLetterQs.map((letterQ) =>
								letterQ === letterAtPosition
									? { ...letterQ, collected: true }
									: letterQ,
							),
						);
					} else {
						const newLength = prevSnake.length - SEGMENTS_TO_REMOVE;
						// Check if game over (only leader left)
						if (newLength <= 1) {
							setGameStatus(GameStatus.GameOver);
							return prevSnake;
						}

						// Play false answer sound
						falseAnswerAudio.play();

						// Update snake
						setSnake((prev) => prev.slice(0, prev.length - SEGMENTS_TO_REMOVE));

						// Pause game briefly to show damage
						setIsRecovering(true);
						setTimeout(() => {
							setIsRecovering(false);
							setIsStopped(true); // Stop snake after recovery
						}, 1000);

						// Decrease vibe by 30%, but set to 5% if it goes to 0 or below
						setVibeLevel((prev) => {
							const newLevel = prev - 30;
							return newLevel <= 0 ? 5 : newLevel;
						});

						// Check if this letter is needed in the word (but just wrong timing)
						const isNeededLetter = currentWord
							.toLowerCase()
							.includes(letterAtPosition.letter.toLowerCase());

						if (isNeededLetter) {
							// This letter is needed! Respawn it somewhere else
							setLetterQs((prevLetterQs) => {
								const usedPositions = new Set<string>();

								// Block safe zone around snake leader
								// 2 cells on top, 2 cells on right, 2 cell on bottom
								const leaderX = snake[0].x;
								const leaderY = snake[0].y;

								// 2 cells above leader
								for (let dy = -2; dy <= 0; dy++) {
									usedPositions.add(`${leaderX},${leaderY + dy}`);
								}

								// 2 cells to the right of leader
								for (let dx = 0; dx <= 2; dx++) {
									usedPositions.add(`${leaderX + dx},${leaderY}`);
								}

								// 2 cell below leader
								for (let dy = 0; dy <= 2; dy++) {
									usedPositions.add(`${leaderX},${leaderY + dy}`);
								}

								// Mark all current positions as used
								prevLetterQs.forEach((lq) => {
									if (!lq.collected && lq !== letterAtPosition) {
										usedPositions.add(`${lq.position.x},${lq.position.y}`);
									}
								});
								snake.forEach((seg) => usedPositions.add(`${seg.x},${seg.y}`));
								hats.forEach((hat) => {
									if (!hat.collected) {
										usedPositions.add(`${hat.position.x},${hat.position.y}`);
									}
								});

								// Find new position for this letter
								let x: number = 0;
								let y: number = 0;
								let attempts = 0;
								do {
									x = Math.floor(Math.random() * GRID_WIDTH);
									y = Math.floor(Math.random() * GRID_HEIGHT);
									attempts++;
									if (attempts > 100) break; // Give up if can't find spot
								} while (usedPositions.has(`${x},${y}`));

								// Update the letter position
								return prevLetterQs.map((letterQ) =>
									letterQ === letterAtPosition
										? { ...letterQ, position: { x, y }, colorSeed: x * 100 + y }
										: letterQ,
								);
							});
						} else {
							// This letter is not needed - remove it permanently
							setLetterQs((prevLetterQs) =>
								prevLetterQs.map((letterQ) =>
									letterQ === letterAtPosition
										? { ...letterQ, collected: true }
										: letterQ,
								),
							);
						}

						return prevSnake;
					}
				}

				// Update snake: grow if letter collected, otherwise move forward
				const newSnake = shouldGrow
					? [newHead, ...prevSnake]
					: [newHead, ...prevSnake.slice(0, -1)];

				return newSnake;
			});
		}, GAME_LOOP_INTERVAL_MS);

		return () => clearInterval(gameLoop);
	}, [
		gameStatus,
		collectedLetters,
		currentWord,
		letterQs,
		hats,
		confettis,
		completedWord,
		isRecovering,
		isStopped,
		waitingForFirstDirection,
		calculateNextHead,
		triggerLetterFlyAnimation,
	]);

	// Check win condition - show word completion celebration
	useEffect(() => {
		if (completedWord !== null) {
			return;
		}

		if (revealedLetters.toLowerCase() === currentWord.toLowerCase() && currentWord.length > 0) {
			const currentQuestion = questions[currentQuestionIndex];
			const playlistId = playList?.id;

			if (playlistId != null && currentQuestion) {
				const elapsedSeconds = (() => {
					if (wordStartTimeRef.current == null) {
						return 0;
					}
					const diffMs = Date.now() - wordStartTimeRef.current;
					return Math.max(0, Math.round(diffMs / 1000));
				})();

				const playlistSectionAnswer = {
					user_learning_path_section_id: playlistId,
					question_id: currentQuestion.id,
					answer_id: null,
					answer_text: collectedLetterSequence,
					answer_time_in_seconds: elapsedSeconds,
				};

				// Check if this is the last question and prepare finish callback
				const isLastQuestion = currentQuestionIndex >= questions.length - 1;
				const freePlayCategoryId = categoryIdFromRoute
					? Number(categoryIdFromRoute)
					: undefined;
				const shouldFinish = isLastQuestion && playlistId != null;
				const finishCallback = shouldFinish
					? () => {
							const score = snakeLength;
							finishPlaylistRequest(
								playlistId,
								freePlayCategoryId,
								EndOutcome.SUCCEEDED,
								score,
							);
					  }
					: undefined;

				sendAnswerRequest({
					playlistId,
					freePlayCategoryId: freePlayCategoryId ?? null,
					data: { playlist_section_answer: playlistSectionAnswer },
					lastQuestion: false,
					callback: finishCallback,
					playtimeInSeconds: elapsedSeconds,
					isCorrect: true, // In Letter Party, completing a word is always correct
				});

				hasAnsweredQuestionsRef.current = true;
			}

			// Word completed! Show celebration and speak the word
			setCompletedWord(currentWord);
			setVibeLevel((prev) => Math.min(100, prev + 10));
			showFireworksConfetti('game-confetti', 2000);

			// Play confetti sound (word audio already played automatically via useWordAudio hook)
			confettiAudio.play();

			// Check if this was the last question (game complete)
			if (currentQuestionIndex >= questions.length - 1) {
				// Game completed! All questions done
				setTimeout(() => {
					setGameStatus(GameStatus.Completed);
				}, 2000);
			} else {
				// After 2 seconds, start next word
				setTimeout(() => {
					setCompletedWord(null);
					setGameStatus(GameStatus.Playing); // Resume playing
					startNewWord(true, true);
				}, 2000);
			}
		}
	}, [
		revealedLetters,
		currentWord,
		completedWord,
		currentQuestionIndex,
		startNewWord,
		questions,
		playList,
		sendAnswerRequest,
		collectedLetterSequence,
		categoryIdFromRoute,
		finishPlaylistRequest,
	]);

	// Vibe decay during gameplay (slow decline if no action) - affects music only, no life loss
	useEffect(() => {
		if (gameStatus !== GameStatus.Playing) return;
		if (waitingForFirstDirection) return; // Don't decay while waiting for first direction
		if (completedWord !== null) return; // Don't decay during celebration

		const decayInterval = setInterval(() => {
			setVibeLevel((prev) => Math.max(0, prev - 2));
		}, DECAY_VIBE_INTERVAL_MS);

		return () => clearInterval(decayInterval);
	}, [gameStatus, waitingForFirstDirection, completedWord]);

	// Show key instructions (immediately on game start, after 3 seconds when stopped)
	useEffect(() => {
		// Clear any existing timer
		if (inactivityTimerRef.current) {
			clearTimeout(inactivityTimerRef.current);
			inactivityTimerRef.current = null;
		}

		// Only show instructions during gameplay when snake is paused
		if (gameStatus !== GameStatus.Playing) {
			setShowKeyInstructions(false);
			return;
		}

		// Show instructions immediately when waiting for first direction (game start)
		if (waitingForFirstDirection) {
			setShowKeyInstructions(true);
		} else if (isStopped) {
			// Show instructions after 3 seconds when snake stops after taking damage
			inactivityTimerRef.current = setTimeout(() => {
				setShowKeyInstructions(true);
			}, 3000);
		} else {
			// Hide instructions when snake is moving
			setShowKeyInstructions(false);
		}

		return () => {
			if (inactivityTimerRef.current) {
				clearTimeout(inactivityTimerRef.current);
				inactivityTimerRef.current = null;
			}
		};
	}, [gameStatus, waitingForFirstDirection, isStopped]);

	const isWordComplete = completedWord !== null;
	const questionsRemaining = Math.max(0, questions.length - currentQuestionIndex);

	const starProgressData = useMemo(() => {
		if (!levelProgress) {
			return null;
		}

		const {
			level_completed,
			level_stars_earned,
			stars_earned: progress_stars_earned,
			current_level,
		} = levelProgress;

		const totalStars = level_completed
			? level_completed?.required_stars
			: current_level?.required_stars;

		const levelCompletedRequiredStars = level_completed?.required_stars;
		const rawEarned = level_stars_earned;
		const earnedStars =
			levelCompletedRequiredStars && rawEarned > levelCompletedRequiredStars
				? levelCompletedRequiredStars
				: rawEarned;

		const previousEarned = level_stars_earned - progress_stars_earned;

		return {
			progressStarsEarned: progress_stars_earned,
			totalStars,
			earnedStars: earnedStars,
			previousEarned: Math.max(0, previousEarned),
		};
	}, [levelProgress]);

	if (showStartScreen) {
		return <StartScreen onStart={handleStartGame} onBack={handleBack} bestScore={bestScore} />;
	}

	if (gameStatus === GameStatus.Completed || gameStatus === GameStatus.GameOver) {
		const currentLevel = levelProgress?.current_level;

		return (
			<EndScreen
				currentScore={snakeLength}
				bestScore={bestScore}
				progressStarsEarned={starProgressData?.progressStarsEarned}
				totalStars={starProgressData?.totalStars}
				earnedStars={starProgressData?.earnedStars}
				previousEarned={starProgressData?.previousEarned}
				levelImage={currentLevel?.level_image}
				levelName={currentLevel?.level_name}
				levelTitle={currentLevel?.level_title}
				levelCompleted={levelProgress?.level_completed}
				onClose={handleBack}
				onReplay={handleReplay}
			/>
		);
	}

	return (
		<div className="letter-party-game">
			<div className="letter-party-game__background">
				<Lottie animationData={waves} loop className="letter-party-game__waves" />
				<SandLeft className="letter-party-game__sand letter-party-game__sand--left" />
				<SandRight className="letter-party-game__sand letter-party-game__sand--right" />
				<SandBottom className="letter-party-game__sand letter-party-game__sand--bottom" />
				<Lottie
					animationData={palmTree}
					loop
					className="letter-party-game__palm-tree letter-party-game__palm-tree--left"
				/>
				<Sandcastle className="letter-party-game__decoration letter-party-game__sandcastle" />
				<DjLevel className="letter-party-game__dj" score={snakeLength} />
				<SwimmingFloat className="letter-party-game__decoration letter-party-game__swimming-float" />
				<Lottie
					animationData={palmTree}
					loop
					className="letter-party-game__palm-tree letter-party-game__palm-tree--right"
				/>
				<Lottie animationData={speaker} loop className="letter-party-game__speaker" />
			</div>

			<div className="letter-party-game__screen">
				<ControlBar
					onBack={handleBack}
					onToggleMute={toggleMute}
					currentWord={currentWord}
					revealedLetters={revealedLetters}
					onSpeak={handlePlayWord}
					wordProgressRef={wordProgressRef}
					isWordComplete={isWordComplete}
					questionsRemaining={questionsRemaining}
					snakeLength={snakeLength}
				/>

				<div className="letter-party-game__swipe-overlay" {...swipeHandlers}>
					<GameBoard
						ref={boardHandleRef}
						gridWidth={GRID_WIDTH}
						gridHeight={GRID_HEIGHT}
						isPortrait={isPortrait}
						isMobile={isMobile}
						canRotateEntities={hasInitializedRef.current}
						snake={snake}
						currentDirection={directionRef.current}
						hatCount={hatCount}
						hats={hats}
						confettis={confettis}
						letterQs={letterQs}
						isRecovering={isRecovering}
						waitingForFirstDirection={waitingForFirstDirection}
						isStopped={isStopped}
						onRotateEntities={handleRotateEntities}
					/>
				</div>

				{flyingLetters.map((animation) => (
					<FlyingLetter
						key={animation.id}
						id={animation.id}
						letter={animation.letter}
						letterIndex={animation.letterIndex}
						from={animation.from}
						to={animation.to}
						onComplete={handleFlyingLetterComplete}
					/>
				))}

				<KeyInstructions show={showKeyInstructions} />
			</div>

			<canvas id="game-confetti" className="letter-party-game__confetti-screen" />
		</div>
	);
}

function mapStateToProps({ playlistReducer, freePlayReducer }: any) {
	return {
		questions: playlistReducer.questions || [],
		playList: playlistReducer.playList,
		levelProgress: freePlayReducer.levelProgress,
		isBridgePlaylist: playlistReducer.isBridgePlaylist,
	};
}

const mapDispatchToProps = {
	sendAnswerRequest: playlistActions.sendAnswerRequest,
	finishPlaylistRequest: playlistActions.finishPlaylistRequest,
	getPlaylistSuccess: playlistActions.getPlaylistSuccess,
};

export default connect(mapStateToProps, mapDispatchToProps)(LetterPartyGame);
